import React from 'react';
import PropTypes from 'prop-types';
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    ComposedChart,
    Bar
} from 'recharts';

const CHART_COLORS = {
    winRate: '#00ff88',
    rollingAvg: '#ffaa00',
    efficiency: '#ff6b6b',
    background: '#2d2d2d',
    grid: '#444',
    text: '#ffffff',
    earlyPhase: 'rgba(0, 255, 136, 0.1)',
    latePhase: 'rgba(255, 170, 0, 0.1)'
};

const SeasonalPerformanceChart = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <div className="chart-container">
                <h3>Seasonal Performance Analysis</h3>
                <div className="no-data">No seasonal performance data available</div>
            </div>
        );
    }

    // Transform and enrich the data
    const chartData = data.map((week, index) => {
        const winRate = week.win_rate || 0;

        // Calculate 4-week rolling average
        const startIndex = Math.max(0, index - 3);
        const rollingWindow = data.slice(startIndex, index + 1);
        const rollingAvg = rollingWindow.reduce((sum, w) => sum + (w.win_rate || 0), 0) / rollingWindow.length;

        // Calculate efficiency ratio for this week
        const bestMargin = week.best_margin || 0;
        const worstMargin = Math.abs(week.worst_margin || 0);
        const efficiency = worstMargin > 0 ? (bestMargin / worstMargin) : 0;

        // Determine season phase
        let phase = 'Mid';
        if (week.week_number <= 4) phase = 'Early';
        else if (week.week_number >= 13) phase = 'Late';

        return {
            week: week.week_label,
            weekNumber: week.week_number,
            winRate: parseFloat(winRate.toFixed(1)),
            rollingAvg: parseFloat(rollingAvg.toFixed(1)),
            efficiency: parseFloat(efficiency.toFixed(2)),
            phase: phase,
            wins: week.wins || 0,
            losses: week.losses || 0,
            totalPicks: week.total_picks || 0,
            bestMargin: bestMargin,
            worstMargin: week.worst_margin || 0
        };
    });

    // Calculate overall statistics
    const overallWinRate = chartData.reduce((sum, week) => sum + week.winRate, 0) / chartData.length;
    const earlyWeeks = chartData.filter(w => w.phase === 'Early');
    const midWeeks = chartData.filter(w => w.phase === 'Mid');
    const lateWeeks = chartData.filter(w => w.phase === 'Late');

    const earlyAvg = earlyWeeks.length > 0 ?
        earlyWeeks.reduce((sum, w) => sum + w.winRate, 0) / earlyWeeks.length : 0;
    const midAvg = midWeeks.length > 0 ?
        midWeeks.reduce((sum, w) => sum + w.winRate, 0) / midWeeks.length : 0;
    const lateAvg = lateWeeks.length > 0 ?
        lateWeeks.reduce((sum, w) => sum + w.winRate, 0) / lateWeeks.length : 0;

    const trend = lateAvg - earlyAvg;
    const avgEfficiency = chartData.reduce((sum, w) => sum + w.efficiency, 0) / chartData.length;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: CHART_COLORS.background,
                    border: `1px solid ${CHART_COLORS.grid}`,
                    color: CHART_COLORS.text,
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                        Week {label} ({data.phase} Season)
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                        📊 Win Rate: <span style={{ color: CHART_COLORS.winRate }}>{data.winRate}%</span>
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                        📈 4-Week Avg: <span style={{ color: CHART_COLORS.rollingAvg }}>{data.rollingAvg}%</span>
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                        ⚡ Efficiency: <span style={{ color: CHART_COLORS.efficiency }}>{data.efficiency}x</span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#ccc' }}>
                        Record: {data.wins}-{data.losses} ({data.totalPicks} picks)
                    </div>
                    <div style={{ fontSize: '11px', color: '#ccc' }}>
                        Best: +{data.bestMargin}, Worst: {data.worstMargin}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="chart-container seasonal-performance-chart">
            <h3>Seasonal Performance Analysis</h3>

            {/* Summary Stats */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                marginBottom: '15px',
                fontSize: '12px',
                color: '#ccc'
            }}>
                <div>
                    <strong>Overall:</strong> {overallWinRate.toFixed(1)}%
                </div>
                <div>
                    <strong>Early (W1-4):</strong> {earlyAvg.toFixed(1)}%
                </div>
                <div>
                    <strong>Mid (W5-12):</strong> {midAvg > 0 ? `${midAvg.toFixed(1)}%` : 'TBD'}
                </div>
                <div>
                    <strong>Late (W13+):</strong> {lateAvg > 0 ? `${lateAvg.toFixed(1)}%` : 'TBD'}
                </div>
                <div>
                    <strong>Trend:</strong>
                    <span style={{
                        color: trend > 0 ? CHART_COLORS.winRate : trend < 0 ? CHART_COLORS.efficiency : '#ccc',
                        marginLeft: '4px'
                    }}>
                        {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                    </span>
                </div>
                <div>
                    <strong>Avg Efficiency:</strong> {avgEfficiency.toFixed(2)}x
                </div>
            </div>

            <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={chartData} margin={{ top: 20, right: 50, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />

                    <XAxis
                        dataKey="week"
                        stroke={CHART_COLORS.text}
                        fontSize={11}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={60}
                    />

                    <YAxis
                        yAxisId="winRate"
                        stroke={CHART_COLORS.text}
                        fontSize={11}
                        domain={[0, 100]}
                        label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                    />

                    <YAxis
                        yAxisId="efficiency"
                        orientation="right"
                        stroke={CHART_COLORS.efficiency}
                        fontSize={11}
                        domain={[0, 'dataMax']}
                        label={{ value: 'Efficiency Ratio', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                    />

                    {/* Phase boundaries */}
                    <ReferenceLine x="W4" stroke="#666" strokeDasharray="2 2" />
                    <ReferenceLine x="W13" stroke="#666" strokeDasharray="2 2" />

                    {/* Overall average line */}
                    <ReferenceLine
                        yAxisId="winRate"
                        y={overallWinRate}
                        stroke="#888"
                        strokeDasharray="5 5"
                        label={{ value: "Season Avg", position: "topRight" }}
                    />

                    <Bar
                        yAxisId="efficiency"
                        dataKey="efficiency"
                        fill={CHART_COLORS.efficiency}
                        opacity={0.3}
                        name="Efficiency Ratio"
                    />

                    <Line
                        yAxisId="winRate"
                        type="monotone"
                        dataKey="winRate"
                        stroke={CHART_COLORS.winRate}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS.winRate, strokeWidth: 2, r: 4 }}
                        name="Weekly Win Rate"
                    />

                    <Line
                        yAxisId="winRate"
                        type="monotone"
                        dataKey="rollingAvg"
                        stroke={CHART_COLORS.rollingAvg}
                        strokeWidth={3}
                        strokeDasharray="3 3"
                        dot={false}
                        name="4-Week Average"
                    />

                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                </ComposedChart>
            </ResponsiveContainer>

            {/* Insights */}
            <div style={{
                marginTop: '10px',
                fontSize: '11px',
                color: '#888',
                lineHeight: '1.4'
            }}>
                <strong>💡 Insights:</strong>
                {trend > 5 && " League performance improves significantly as season progresses - possibly due to better line reading or weaker late-season lines."}
                {trend < -5 && " League performance declines as season progresses - lines may get tighter or early season had more obvious picks."}
                {Math.abs(trend) <= 5 && " League performance remains relatively consistent throughout the season."}
                {avgEfficiency > 1.5 && " League tends to win big and lose small - good at identifying strong picks."}
                {avgEfficiency < 0.8 && " League tends to win small and lose big - may be taking too many risky picks."}
            </div>
        </div>
    );
};

SeasonalPerformanceChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        week_label: PropTypes.string,
        week_number: PropTypes.number,
        win_rate: PropTypes.number,
        wins: PropTypes.number,
        losses: PropTypes.number,
        total_picks: PropTypes.number,
        best_margin: PropTypes.number,
        worst_margin: PropTypes.number
    }))
};

export default SeasonalPerformanceChart;
